import {
  CombinedGraphQLErrors,
  ServerError,
  ServerParseError
} from '@apollo/client';
import { useCallback, useContext } from 'react';

import type {
  DataTableDownloadFieldsFragment,
  DataTableFormat
} from '../__generated__/apollo-hooks';
import {
  isDataTableADataTableError,
  isDataTableADataTableFinished,
  isDataTableADataTableProcessing,
  isOrganizationChangesetAOrganizationRecipeRunCanceled,
  isOrganizationChangesetAOrganizationRecipeRunError,
  isOrganizationChangesetAOrganizationRecipeRunFinished,
  isOrganizationChangesetAOrganizationSearch,
  useDataTableDownloadStatusLazyQuery,
  useDownloadDataTableMutation,
  useRecipeRunHeartbeatLazyQuery
} from '../__generated__/apollo-hooks';
import type { DataTableRef } from '../components/data-tables-view/data-tables-view.types';
import { ERROR_MESSAGES } from '../constants/error-messages';
import type { DataTableDownloadSource } from '../contexts/downloads.context';
import { DownloadsContext, ManifestKind } from '../contexts/downloads.context';
import { NotificationContext } from '../contexts/notification.context';
import {
  logAndThrow,
  pollUntilDeadline
} from '../helpers/download-manifest.helper';
import { getDetailedErrorMessage } from '../helpers/graphql-error.helper';
import { isSearchNode, isSearchRunning } from '../helpers/recipe-run.helper';
import { toEpochMs } from '../helpers/time.helper';

type DownloadDataTableParams = {
  /** The organization the caller loaded the run under; the poll looks the run up there. */
  organizationId: string;
  recipeRunId: string;
  dataTableName: string;
  displayName: string;
  format: DataTableFormat;
  group: string | null;
};

type UseDataTableDownloadReturn = {
  downloadDataTable: (params: DownloadDataTableParams) => void;
};

/**
 * Hook for downloading data tables with polling support.
 * Integrates with DownloadsContext to show progress in the download manager.
 *
 * This only describes the job. Starting and polling it is
 * {@link useResolveDataTableDownloadUrl}, which the download manager runs —
 * the description is serializable, so a reload can hand the job back to that
 * resolver instead of losing it.
 */
export const useDataTableDownload = (): UseDataTableDownloadReturn => {
  const { manageDownload } = useContext(DownloadsContext);

  const downloadDataTable = useCallback(
    ({
      organizationId,
      recipeRunId,
      dataTableName,
      displayName,
      format,
      group
    }: DownloadDataTableParams) => {
      const id = `data-table-${dataTableName}-${Date.now()}`;
      const fileName = `${displayName || dataTableName}.${format.toLowerCase()}`;

      manageDownload({
        id,
        fileName,
        kind: ManifestKind.AUTHENTICATED,
        source: {
          type: 'DATA_TABLE',
          organizationId,
          recipeRunId,
          dataTable: dataTableName,
          format,
          group: group ?? undefined
        }
      });
    },
    [manageDownload]
  );

  return { downloadDataTable };
};

/** Named `<last name segment>-<runId>` so two runs of the same table don't collide. */
export const csvDownloadParams = (
  organizationId: string,
  recipeRunId: string,
  table: DataTableRef
): DownloadDataTableParams => {
  const shortName = table.name.split('.').at(-1) ?? table.name;
  return {
    organizationId,
    recipeRunId,
    dataTableName: table.name,
    displayName: `${shortName}-${recipeRunId}`,
    format: 'CSV',
    group: table.group
  };
};

/**
 * CSV in one click.
 *
 * Takes the whole `DataTableRef` rather than its parts: that type exists to
 * keep `name` and `group` together, because `downloadDataTable` strict-equals
 * `group` (#8591). No-ops when the table is unknown, so a caller cannot fall
 * through to a defaulted group.
 */
export const useDataTableCsvDownload = ({
  organizationId,
  recipeRunId,
  table
}: {
  organizationId: string;
  recipeRunId: string;
  table: DataTableRef | undefined;
}): (() => void) => {
  const { downloadDataTable } = useDataTableDownload();

  return useCallback(() => {
    if (!table) {
      return;
    }
    downloadDataTable(csvDownloadParams(organizationId, recipeRunId, table));
  }, [organizationId, recipeRunId, table, downloadDataTable]);
};

const processingStartOf = (
  dataTable: DataTableDownloadFieldsFragment
): number | undefined => {
  if (isDataTableADataTableProcessing(dataTable)) {
    return toEpochMs(dataTable.processingStartedAt);
  }

  return isDataTableADataTableFinished(dataTable)
    ? toEpochMs(dataTable.finishedStartedAt)
    : undefined;
};

/** Consecutive sweep checks, about a minute's worth, that may fail to reach the server. */
const SWEEP_CHECK_BLIPS = 20;

const reachedServer = (error: unknown) =>
  CombinedGraphQLErrors.is(error) ||
  ServerError.is(error) ||
  ServerParseError.is(error);

type ResolveDataTableUrl = (
  source: DataTableDownloadSource,
  startTimestamp: number,
  onSourceResolved: (source: DataTableDownloadSource) => void,
  signal?: AbortSignal
) => Promise<string>;

/**
 * Starts a data table download, or resumes one the server is already running:
 *
 * 1. With no `downloadId`, check the changeset and, for a saved search, wait
 *    out its sweep, as moderne-saas merges its table only when the sweep ends;
 *    then call the downloadDataTable mutation and report the id it returns so
 *    the job can be resumed after a reload
 * 2. Poll dataTableDownloadStatus until Finished or Error, reporting when a
 *    worker picks the job up so the timer skips time spent queued
 * 3. Resolve with the downloadUrl from the Finished state
 *
 * `organizationId` comes from the job rather than the selected-organization
 * store, so a job resumed after switching organizations is still polled
 * against the one that started it.
 */
export const useResolveDataTableDownloadUrl = (): ResolveDataTableUrl => {
  const [downloadMutation] = useDownloadDataTableMutation();
  const [pollStatus] = useDataTableDownloadStatusLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [checkSweep] = useRecipeRunHeartbeatLazyQuery({
    fetchPolicy: 'no-cache'
  });
  const { renderNotification } = useContext(NotificationContext);

  return useCallback(
    async (source, startTimestamp, onSourceResolved, signal) => {
      let current = source;
      let sweepError: string | null = null;
      const served = (url: string) => {
        if (sweepError) {
          renderNotification(
            'warning',
            `The saved search didn’t finish, so its CSV may miss matches. ${sweepError}`
          );
        }
        return url;
      };

      if (!current.downloadId) {
        let blips = 0;
        const sweep = await pollUntilDeadline(
          startTimestamp,
          async () => {
            const { data, error } = await checkSweep({
              variables: {
                organizationId: source.organizationId,
                recipeRunId: source.recipeRunId
              }
            });
            const node = data?.organization?.changesets?.edges?.at(0)?.node;
            if (error && !node && !reachedServer(error)) {
              return blips++ < SWEEP_CHECK_BLIPS
                ? null
                : logAndThrow(getDetailedErrorMessage(error));
            }
            if (!node) {
              // A sweep still running fails the mutation rather than serve a partial table.
              return { error: null };
            }
            blips = 0;
            if (!isSearchNode(node)) {
              return { error: null };
            }
            if (error) {
              return logAndThrow(getDetailedErrorMessage(error));
            }
            if (isSearchRunning(node)) {
              return null;
            }
            // A stopped sweep kept what it found; its error only explains a table it never wrote.
            return { error: node.error };
          },
          signal,
          ERROR_MESSAGES.downloads.sweepTimedOut.title
        );
        sweepError = sweep.error;
        const explain = (message: string) =>
          sweepError
            ? `The saved search didn’t finish. ${sweepError}`
            : message;

        const response = await downloadMutation({
          variables: {
            changesetId: source.recipeRunId,
            dataTable: source.dataTable,
            group: source.group,
            format: source.format
          }
        });

        const downloadResult = response.data?.downloadDataTable;

        if (!downloadResult) {
          return logAndThrow(
            explain(ERROR_MESSAGES.downloads.initiateFailed.title)
          );
        }

        // If already finished (cached result), return the URL directly
        if (isDataTableADataTableFinished(downloadResult)) {
          return served(downloadResult.downloadUrl);
        }

        if (isDataTableADataTableError(downloadResult)) {
          return logAndThrow(
            explain(
              downloadResult.message ?? ERROR_MESSAGES.downloads.failed.title
            )
          );
        }

        current = {
          ...source,
          downloadId: downloadResult.id,
          processingStartTimestamp: processingStartOf(downloadResult)
        };
        onSourceResolved(current);
      }

      const url = await pollUntilDeadline(
        startTimestamp,
        async () => {
          const { data: statusData, error } = await pollStatus({
            variables: {
              organizationId: source.organizationId,
              recipeRunId: source.recipeRunId,
              downloadId: current.downloadId
            }
          });

          if (error) {
            return logAndThrow(getDetailedErrorMessage(error));
          }

          const node = statusData?.organization?.changesets?.edges?.at(0)?.node;

          if (!node) {
            return logAndThrow(ERROR_MESSAGES.downloads.jobNotFound.title);
          }

          // DataTableDownloadStatusFields selects `dataTables` only on these
          // three run types, and the query adds it for a search — a run still
          // in progress legitimately has no dataTables field to read yet.
          if (
            isOrganizationChangesetAOrganizationRecipeRunFinished(node) ||
            isOrganizationChangesetAOrganizationRecipeRunCanceled(node) ||
            isOrganizationChangesetAOrganizationRecipeRunError(node) ||
            isOrganizationChangesetAOrganizationSearch(node)
          ) {
            const dataTableNode = node.dataTables?.edges?.at(0)?.node;

            if (!dataTableNode) {
              return logAndThrow(ERROR_MESSAGES.downloads.jobNotFound.title);
            }

            if (!current.processingStartTimestamp) {
              const processingStartTimestamp = processingStartOf(dataTableNode);

              if (processingStartTimestamp) {
                current = { ...current, processingStartTimestamp };
                onSourceResolved(current);
              }
            }

            if (isDataTableADataTableFinished(dataTableNode)) {
              return dataTableNode.downloadUrl;
            }

            if (isDataTableADataTableError(dataTableNode)) {
              return logAndThrow(
                dataTableNode.message ?? ERROR_MESSAGES.downloads.failed.title
              );
            }
          }

          return null;
        },
        signal
      );
      return served(url);
    },
    [downloadMutation, pollStatus, checkSweep, renderNotification]
  );
};
